import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  useColorModeValue,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Avatar,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { FiCheck, FiX, FiMail, FiMapPin, FiPhone, FiUser, FiCalendar } from 'react-icons/fi';
import Icon from 'components/icons/Icon';
import { adminService, PendingAgencyData } from '../../../services/adminService';

function AgencyApprovals() {
  const [pendingAgencies, setPendingAgencies] = useState<PendingAgencyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState<PendingAgencyData | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: number]: boolean }>({});
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Chakra UI theme colors
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.600", "gray.400");
  const bg = useColorModeValue("white", "navy.700");
  const cardShadow = useColorModeValue("0px 4px 20px rgba(0, 0, 0, 0.08)", "0px 4px 20px rgba(0, 0, 0, 0.2)");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Load pending agencies
  useEffect(() => {
    const loadPendingAgencies = async () => {
      try {
        setLoading(true);
        const agencies = await adminService.getPendingAgencies();
        setPendingAgencies(agencies);
      } catch (error: any) {
        console.error('Failed to load pending agencies:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load pending agencies",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPendingAgencies();
  }, [toast]);

  const handleApprove = async (agencyId: number) => {
    setActionLoading(prev => ({ ...prev, [agencyId]: true }));
    
    try {
      const result = await adminService.approveAgency(agencyId);
      
      if (result.success) {
        setPendingAgencies(prev => prev.filter(agency => agency.id !== agencyId));
        
        toast({
          title: "✅ Agency Approved",
          description: result.message || "Agency has been approved and activated successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to approve agency.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Failed to approve agency:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve agency. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [agencyId]: false }));
    }
  };

  const handleReject = async (agencyId: number) => {
    setActionLoading(prev => ({ ...prev, [agencyId]: true }));
    
    try {
      const result = await adminService.rejectAgency(agencyId, "Application did not meet requirements");
      
      if (result.success) {
        setPendingAgencies(prev => prev.filter(agency => agency.id !== agencyId));
        
        toast({
          title: "❌ Agency Rejected",
          description: result.message || "Agency application has been rejected and removed.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to reject agency.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Failed to reject agency:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reject agency. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [agencyId]: false }));
    }
  };

  const openDetailsModal = (agency: PendingAgencyData) => {
    setSelectedAgency(agency);
    onOpen();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4}>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="brand.500" />
            <Text color={textColorSecondary}>Loading pending approvals...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4}>
      <VStack spacing={6} align="stretch" maxW="1200px" mx="auto">
        {/* Header - removed duplicate title since navbar shows it */}
        <Box>
          <Text color={textColorSecondary} fontSize="lg">
            Review and approve agency registration applications
          </Text>
        </Box>

        {/* Statistics */}
        <Card 
          bg={bg} 
          boxShadow={cardShadow} 
          borderRadius="16px" 
          border="1px solid" 
          borderColor={borderColor}
        >
          <CardBody p={6}>
            <Flex justify="center" align="center">
              <VStack spacing={3}>
                <Box 
                  p={4} 
                  borderRadius="12px" 
                  bg={useColorModeValue("orange.500", "orange.600")}
                  textAlign="center"
                  minW="70px"
                >
                  <Text 
                    fontSize="3xl" 
                    fontWeight="bold" 
                    color="white"
                  >
                    {pendingAgencies.length}
                  </Text>
                </Box>
                <Text fontSize="md" color={textColor} textAlign="center" fontWeight="600">
                  Pending Approvals
                </Text>
                <Text fontSize="sm" color={textColorSecondary} textAlign="center">
                  Applications awaiting review
                </Text>
              </VStack>
            </Flex>
          </CardBody>
        </Card>

        {/* Pending Agencies List */}
        {pendingAgencies.length === 0 ? (
          <Card 
            bg={bg} 
            boxShadow={cardShadow} 
            borderRadius="16px" 
            border="1px solid" 
            borderColor={borderColor}
          >
            <CardBody p={6}>
              <Alert 
                status="info" 
                borderRadius="12px" 
                bg={useColorModeValue("blue.50", "blue.900")}
                border="1px solid"
                borderColor={useColorModeValue("blue.200", "blue.600")}
              >
                <AlertIcon color="blue.500" />
                <VStack align="start" spacing={2}>
                  <Text fontWeight="600" fontSize="md" color={textColor}>No pending approvals</Text>
                  <Text fontSize="sm" color={textColorSecondary}>
                    All agency applications have been processed. New applications will appear here for review.
                  </Text>
                </VStack>
              </Alert>
            </CardBody>
          </Card>
        ) : (
          <VStack spacing={5} align="stretch">
            {pendingAgencies.map((agency) => (
              <Card 
                key={agency.id} 
                bg={bg} 
                boxShadow={cardShadow}
                borderRadius="16px"
                border="1px solid"
                borderColor={borderColor}
                _hover={{
                  boxShadow: useColorModeValue("0px 8px 25px rgba(0, 0, 0, 0.12)", "0px 8px 25px rgba(0, 0, 0, 0.3)"),
                  transform: "translateY(-2px)",
                  borderColor: useColorModeValue("blue.300", "blue.500")
                }}
                transition="all 0.2s ease"
              >
                <CardBody p={{ base: 4, md: 6 }}>
                  <Flex justify="space-between" align="start" wrap="wrap" gap={{ base: 4, md: 6 }} direction={{ base: "column", lg: "row" }}>
                    <VStack align="start" spacing={4} flex="1" minW={{ base: "100%", lg: "300px" }}>
                      {/* Agency Header */}
                      <HStack spacing={4} w="full">
                        <Avatar 
                          name={agency.name} 
                          bg={useColorModeValue("blue.500", "blue.600")}
                          color="white"
                          size={{ base: "md", md: "lg" }}
                          fontWeight="bold"
                          fontSize={{ base: "md", md: "xl" }}
                        />
                        <VStack align="start" spacing={1} flex="1">
                          <Heading size={{ base: "sm", md: "md" }} color={textColor} fontWeight="600">
                            {agency.name}
                          </Heading>
                          <Badge 
                            colorScheme="orange" 
                            size="sm" 
                            borderRadius="full"
                            px={3}
                            py={1}
                            fontWeight="500"
                          >
                            Pending Approval
                          </Badge>
                        </VStack>
                      </HStack>

                      {/* Agency Info */}
                      <Box 
                        p={4} 
                        bg={useColorModeValue("gray.50", "navy.800")} 
                        borderRadius="10px"
                        w="full"
                        border="1px solid"
                        borderColor={useColorModeValue("gray.200", "gray.700")}
                      >
                        <Text color={textColor} fontSize="sm" noOfLines={2} lineHeight="1.5" fontWeight="500">
                          {agency.description}
                        </Text>
                      </Box>

                      <VStack align="start" spacing={3} fontSize="sm" w="full">
                        <HStack spacing={3}>
                          <Box color="blue.500">
                            <Icon as={FiUser} />
                          </Box>
                          <Text color={textColorSecondary}>
                            <Text as="span" fontWeight="500" color={textColor}>Owner:</Text> {agency.owner.first_name} {agency.owner.last_name}
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box color="green.500">
                            <Icon as={FiMail} />
                          </Box>
                          <Text color={textColorSecondary}>
                            {agency.contact_email}
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box color="purple.500">
                            <Icon as={FiCalendar} />
                          </Box>
                          <Text color={textColorSecondary}>
                            <Text as="span" fontWeight="500" color={textColor}>Applied:</Text> {formatDate(agency.created_at)}
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>

                    {/* Actions */}
                    <VStack spacing={3} minW={{ base: "100%", lg: "200px" }} w={{ base: "100%", lg: "auto" }}>
                      <Button
                        size={{ base: "sm", md: "md" }}
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => openDetailsModal(agency)}
                        w="full"
                        borderRadius="10px"
                        fontWeight="500"
                        _hover={{
                          bg: useColorModeValue("blue.50", "blue.900"),
                          borderColor: "blue.300"
                        }}
                      >
                        View Details
                      </Button>
                      
                      <HStack spacing={3} w="full">
                        <Button
                          leftIcon={<Icon as={FiCheck} />}
                          colorScheme="green"
                          size={{ base: "sm", md: "md" }}
                          onClick={() => handleApprove(agency.id)}
                          isLoading={actionLoading[agency.id]}
                          loadingText="Approving..."
                          flex="1"
                          borderRadius="10px"
                          fontWeight="500"
                        >
                          Approve
                        </Button>
                        <Button
                          leftIcon={<Icon as={FiX} />}
                          colorScheme="red"
                          variant="outline"
                          size={{ base: "sm", md: "md" }}
                          onClick={() => handleReject(agency.id)}
                          isLoading={actionLoading[agency.id]}
                          loadingText="Rejecting..."
                          flex="1"
                          borderRadius="10px"
                          fontWeight="500"
                          _hover={{
                            bg: useColorModeValue("red.50", "red.900"),
                            borderColor: "red.300"
                          }}
                        >
                          Reject
                        </Button>
                      </HStack>
                    </VStack>
                  </Flex>
                </CardBody>
              </Card>
            ))}
          </VStack>
        )}

        {/* Details Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "xl" }}>
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <ModalContent borderRadius={{ base: "0", md: "16px" }} mx={{ base: 0, md: 4 }} my={{ base: 0, md: 4 }} bg={bg}>
            <ModalHeader pb={2} px={{ base: 4, md: 6 }}>
              <VStack align="start" spacing={3}>
                <Heading size={{ base: "md", md: "lg" }} color={textColor}>Agency Application Details</Heading>
                {selectedAgency && (
                  <Badge 
                    colorScheme="orange" 
                    size="lg" 
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontWeight="600"
                  >
                    Pending Approval
                  </Badge>
                )}
              </VStack>
            </ModalHeader>
            <ModalCloseButton 
              borderRadius="full"
              _hover={{
                bg: useColorModeValue("gray.100", "gray.700")
              }}
            />
            <ModalBody py={4} px={{ base: 4, md: 6 }}>
              {selectedAgency && (
                <VStack spacing={5} align="stretch">
                  {/* Agency Information Card */}
                  <Card bg={useColorModeValue("gray.50", "navy.800")} borderRadius="12px" border="1px solid" borderColor={borderColor}>
                    <CardHeader pb={2}>
                      <Heading size="md" color={textColor} display="flex" alignItems="center" gap={3}>
                        <Box color="blue.500">
                          <Icon as={FiUser} />
                        </Box>
                        Agency Information
                      </Heading>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        <HStack>
                          <Text minW="120px" fontSize="sm" color={textColorSecondary} fontWeight="500">
                            Name:
                          </Text>
                          <Text fontSize="sm" fontWeight="600" color={textColor}>
                            {selectedAgency.name}
                          </Text>
                        </HStack>
                        <VStack align="start" spacing={2}>
                          <Text fontSize="sm" color={textColorSecondary} fontWeight="500">
                            Description:
                          </Text>
                          <Box 
                            p={3} 
                            bg={useColorModeValue("white", "navy.700")} 
                            borderRadius="8px" 
                            w="full"
                            border="1px solid"
                            borderColor={borderColor}
                          >
                            <Text fontSize="sm" lineHeight="1.5" color={textColor}>
                              {selectedAgency.description}
                            </Text>
                          </Box>
                        </VStack>
                        <HStack align="start">
                          <HStack minW="120px">
                            <Box color="red.500">
                              <Icon as={FiMapPin} />
                            </Box>
                            <Text fontSize="sm" color={textColorSecondary} fontWeight="500">
                              Address:
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color={textColor}>
                            {selectedAgency.address}
                          </Text>
                        </HStack>
                        {selectedAgency.contact_phone && (
                          <HStack>
                            <HStack minW="120px">
                              <Box color="green.500">
                                <Icon as={FiPhone} />
                              </Box>
                              <Text fontSize="sm" color={textColorSecondary} fontWeight="500">
                                Phone:
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color={textColor}>
                              {selectedAgency.contact_phone}
                            </Text>
                          </HStack>
                        )}
                        <HStack>
                          <HStack minW="120px">
                            <Box color="purple.500">
                              <Icon as={FiMail} />
                            </Box>
                            <Text fontSize="sm" color={textColorSecondary} fontWeight="500">
                              Contact:
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color={textColor}>
                            {selectedAgency.contact_email}
                          </Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Owner Information Card */}
                  <Card bg={useColorModeValue("gray.50", "navy.800")} borderRadius="12px" border="1px solid" borderColor={borderColor}>
                    <CardHeader pb={2}>
                      <Heading size="md" color={textColor} display="flex" alignItems="center" gap={3}>
                        <Avatar 
                          name={`${selectedAgency.owner.first_name} ${selectedAgency.owner.last_name}`}
                          size="sm"
                          bg="brand.500"
                        />
                        Owner Information
                      </Heading>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        <HStack>
                          <Text minW="120px" fontSize="sm" color={textColorSecondary} fontWeight="500">
                            Name:
                          </Text>
                          <Text fontSize="sm" fontWeight="600" color={textColor}>
                            {selectedAgency.owner.first_name} {selectedAgency.owner.last_name}
                          </Text>
                        </HStack>
                        <HStack>
                          <Text minW="120px" fontSize="sm" color={textColorSecondary} fontWeight="500">
                            Email:
                          </Text>
                          <Text fontSize="sm" color={textColor}>
                            {selectedAgency.owner.email}
                          </Text>
                        </HStack>
                        <HStack>
                          <Text minW="120px" fontSize="sm" color={textColorSecondary} fontWeight="500">
                            Applied:
                          </Text>
                          <Badge colorScheme="blue" borderRadius="full">
                            {formatDate(selectedAgency.created_at)}
                          </Badge>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              )}
            </ModalBody>

            <ModalFooter pt={4} px={{ base: 4, md: 6 }}>
              <VStack spacing={3} w="full">
                <HStack spacing={3} w="full" justify={{ base: "stretch", md: "end" }} flexWrap={{ base: "wrap", md: "nowrap" }}>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={onClose}
                    borderRadius="10px"
                    fontWeight="500"
                    size={{ base: "md", md: "md" }}
                    flex={{ base: "1", md: "none" }}
                    order={{ base: 3, md: 1 }}
                  >
                    Close
                  </Button>
                  <Button
                    leftIcon={<Icon as={FiX} />}
                    colorScheme="red"
                    variant="outline"
                    onClick={() => {
                      if (selectedAgency) {
                        handleReject(selectedAgency.id);
                        onClose();
                      }
                    }}
                    isLoading={selectedAgency ? actionLoading[selectedAgency.id] : false}
                    loadingText="Rejecting..."
                    borderRadius="10px"
                    fontWeight="500"
                    size={{ base: "md", md: "md" }}
                    flex={{ base: "1", md: "none" }}
                    order={{ base: 2, md: 2 }}
                  >
                    Reject
                  </Button>
                  <Button
                    leftIcon={<Icon as={FiCheck} />}
                    colorScheme="green"
                    onClick={() => {
                      if (selectedAgency) {
                        handleApprove(selectedAgency.id);
                        onClose();
                      }
                    }}
                    isLoading={selectedAgency ? actionLoading[selectedAgency.id] : false}
                    loadingText="Approving..."
                    borderRadius="10px"
                    fontWeight="500"
                    size={{ base: "md", md: "md" }}
                    flex={{ base: "1", md: "none" }}
                    order={{ base: 1, md: 3 }}
                  >
                    <Text display={{ base: "none", sm: "block" }}>Approve Agency</Text>
                    <Text display={{ base: "block", sm: "none" }}>Approve</Text>
                  </Button>
                </HStack>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}

export default AgencyApprovals;